import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, HandCoins } from "lucide-react";
import api from "../lib/api";
import Avatar from "../components/Avatar";
import { formatAED, formatDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const STATUS_TONE = {
  active: "text-[#C9A84C]",
  delivered: "text-[#D4B45A]",
  invoiced: "text-[#E1C97A]",
  paid: "text-emerald-400",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-[0.25em] ${STATUS_TONE[status] || "text-[#9A9590]"}`}>
      ● {status?.replace("_", " ")}
    </span>
  );
}

export default function Deals() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [members, setMembers] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const [d, m] = await Promise.all([api.get("/deals"), api.get("/members")]);
    setDeals(d.data);
    setMembers(m.data);
  };
  useEffect(() => {
    document.title = "Deals · The Synergy Table";
    load();
  }, []);

  return (
    <div className="space-y-8" data-testid="deals-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline">Active</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Deals</h1>
          <p className="text-[#B8B3AC] mt-3 max-w-xl">
            Live execution. Coordinator, contributors, milestones, value — everything we're working on together.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-gold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm self-start" data-testid="new-deal-btn">
          <Plus size={16} /> Log a Deal
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {deals.map((d) => {
          const total = d.milestones?.length || 0;
          const done = d.milestones?.filter((m) => m.done).length || 0;
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
            <Link key={d.id} to={`/deals/${d.id}`} className="ts-card p-7 hover:-translate-y-0.5 transition-transform duration-300 block" data-testid={`deal-card-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="overline">{d.client_name || "Confidential"}</div>
                  <h3 className="font-serif text-2xl text-[#F0EDE8] mt-2 leading-snug">{d.name}</h3>
                </div>
                <StatusBadge status={d.status} />
              </div>
              <div className="mt-4 flex items-center gap-5 text-sm text-[#9A9590]">
                <span className="font-serif text-2xl text-[#C9A84C]">{formatAED(d.estimated_value)}</span>
                <span className="text-xs uppercase tracking-widest">Target {formatDate(d.target_close_date)}</span>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#9A9590]">
                  <span>Progress</span>
                  <span>{done}/{total} milestones</span>
                </div>
                <div className="mt-2 h-1 bg-[#0D0D0D] rounded-full overflow-hidden">
                  <div className="h-full bg-[#C9A84C]" style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Avatar name={d.coordinator?.name} photoUrl={d.coordinator?.photo_url} color={d.coordinator?.avatar_color} size={28} />
                  <div className="text-xs text-[#9A9590]">
                    Lead: <span className="text-[#F0EDE8]">{d.coordinator?.name}</span>
                  </div>
                </div>
                <div className="flex -space-x-2">
                  {(d.contributors || []).slice(0, 4).map((c) => (
                    <Avatar key={c.id} name={c.name} photoUrl={c.photo_url} color={c.avatar_color} size={26} className="ring-2 ring-[#1A1A1A]" />
                  ))}
                </div>
              </div>
            </Link>
          );
        })}
        {deals.length === 0 && (
          <div className="col-span-full ts-card p-12 text-center" data-testid="deals-empty">
            <HandCoins size={28} className="text-[#C9A84C]/60 mx-auto mb-4" strokeWidth={1.5}/>
            <div className="font-serif text-xl text-[#F0EDE8]">No active deals yet.</div>
            <p className="text-sm text-[#B8B3AC] mt-2">Post an opportunity to get started.</p>
            <Link to="/opportunities" className="btn-ghost-gold rounded-md px-4 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest mt-5">
              Go to Opportunities →
            </Link>
          </div>
        )}
      </div>

      {showNew && <NewDealModal members={members} userId={user.id} onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}

function NewDealModal({ members, userId, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "",
    client_name: "",
    description: "",
    coordinator_id: userId,
    contributing_member_ids: [],
    estimated_value: "",
    target_close_date: "",
  });

  const toggleContrib = (id) =>
    setF((p) => ({
      ...p,
      contributing_member_ids: p.contributing_member_ids.includes(id)
        ? p.contributing_member_ids.filter((x) => x !== id)
        : [...p.contributing_member_ids, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/deals", {
      ...f,
      estimated_value: f.estimated_value ? Number(f.estimated_value) : null,
      start_date: new Date().toISOString(),
    });
    onClose();
    onCreated();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ts-card p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="deal-modal">
        <div className="overline">New deal</div>
        <h2 className="font-serif text-2xl text-[#F0EDE8] mt-2 mb-6">Log a deal</h2>
        <form onSubmit={submit} className="space-y-5">
          <div><label className="overline">Deal name</label><input className="ts-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="deal-name" /></div>
          <div><label className="overline">Client</label><input className="ts-input" placeholder="Initials or codename" value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} /></div>
          <div><label className="overline">Description</label><textarea className="ts-input min-h-[80px] py-2" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline">Estimated value (AED)</label><input type="number" className="ts-input" value={f.estimated_value} onChange={(e) => setF({ ...f, estimated_value: e.target.value })} /></div>
            <div><label className="overline">Target close</label><input type="date" className="ts-input" value={f.target_close_date} onChange={(e) => setF({ ...f, target_close_date: e.target.value })} /></div>
          </div>
          <div>
            <label className="overline">Contributors</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {members.filter((m) => m.id !== f.coordinator_id).map((m) => (
                <button type="button" key={m.id} onClick={() => toggleContrib(m.id)} className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full transition ${f.contributing_member_ids.includes(m.id) ? "bg-[#C9A84C] text-[#0D0D0D]" : "border border-[#C9A84C]/25 text-[#9A9590]"}`}>
                  {m.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-sm text-[#9A9590]">Cancel</button>
            <button type="submit" className="btn-gold rounded-md px-5 py-2.5 text-sm" data-testid="deal-submit">Log deal</button>
          </div>
        </form>
      </div>
    </div>
  );
}
